import { useState, useCallback } from 'react';

export interface AlertDialogState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string;
  variant: 'danger' | 'warning' | 'info' | 'success';
  confirmButtonVariant: 'primary' | 'danger' | 'warning';
  onConfirm: () => void;
}

const defaultState: AlertDialogState = {
  isOpen: false,
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  variant: 'warning',
  confirmButtonVariant: 'primary',
  onConfirm: () => {},
};

export interface ShowAlertOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'success';
  confirmButtonVariant?: 'primary' | 'danger' | 'warning';
  onConfirm: () => void;
}

export function useAlertDialog() {
  const [alertState, setAlertState] = useState<AlertDialogState>(defaultState);

  const showAlert = useCallback((options: ShowAlertOptions) => {
    setAlertState({
      isOpen: true,
      title: options.title,
      message: options.message,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      variant: options.variant || 'warning',
      confirmButtonVariant: options.confirmButtonVariant || 'primary',
      onConfirm: options.onConfirm,
    });
  }, []);

  const closeAlert = useCallback(() => {
    setAlertState(prev => ({ ...prev, isOpen: false }));
  }, []);

  // Convenience methods for common dialogs
  const confirmDelete = useCallback((itemName: string, onConfirm: () => void) => {
    showAlert({
      title: 'Delete Item',
      message: `Are you sure you want to delete "${itemName}"? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      variant: 'danger',
      confirmButtonVariant: 'danger',
      onConfirm,
    });
  }, [showAlert]);

  const confirmAction = useCallback((title: string, message: string, onConfirm: () => void) => {
    showAlert({
      title,
      message,
      confirmText: 'Confirm',
      cancelText: 'Cancel',
      variant: 'warning',
      confirmButtonVariant: 'primary',
      onConfirm,
    });
  }, [showAlert]);

  return {
    alertState,
    showAlert,
    closeAlert,
    confirmDelete,
    confirmAction,
  };
}
